import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { USER_ROLES } from 'constants/common';

import {
  getStudentById,
  issueStudentCertificate,
} from 'store/student/actions';
import { currentStudentSelector } from 'store/student/selectors';
import { userSelector } from 'store/auth/selectors';

import { useModal } from 'hooks/useModal';

import { EditStudent } from './components/EditStudent';
import { GrantProAccess } from './components/GrantProAccess';

import { School } from './components/School';
import { ManageCoursesAccess } from './components/ManageCoursesAccess';
import { showToast } from 'helpers/showToast';
import { getTranslation } from 'helpers/getTranslation';
import { getUnknownErrorMessage } from 'helpers/unknownErrorMessage';

import { isCourseFullyCompleted } from './components/CoursesSection/CoursesSection.helpers';

import {
  getStudentSchools,
  getLastUpdatedByCourses,
} from './StudentPage.helpers';

import StudentPage from './StudentPage';

const PRESENCE_POLL_INTERVAL_MS = 30_000;

const StudentPageContainer = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const { id: studentId } = useParams();

  const user = useAppSelector(userSelector);
  const currentStudent = useAppSelector(currentStudentSelector);

  const [loading, setLoading] = useState(true);

  const [editStudentOpen, openEditStudent, closeEditStudent] = useModal({
    defaultOpen: false,
  });
  const [
    grantProAccessOpen,
    openGrantProAccess,
    closeGrantProAccess,
    grantProAccessData,
  ] = useModal({
    defaultOpen: false,
  });
  const [
    manageCoursesAccessOpen,
    openManageCoursesAccess,
    closeManageCoursesAccess,
    manageCoursesAccessData,
  ] = useModal({
    defaultOpen: false,
  });
  const [issuingCertificateCourseId, setIssuingCertificateCourseId] = useState<
    string | null
  >(null);

  useEffect(() => {
    const _getStudentById = async () => {
      try {
        await dispatch(getStudentById(studentId)).unwrap();
      } catch (error) {
        console.warn(error);
      }

      setLoading(false);
    };

    _getStudentById();
  }, []);

  useEffect(() => {
    if (loading || !studentId) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void dispatch(getStudentById(studentId));
    }, PRESENCE_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [dispatch, loading, studentId]);

  const handleEditClick = () => {
    openEditStudent();
  };

  const handleGrantProAccessClick = (school: any) => {
    openGrantProAccess({ schoolId: school.id });
  };

  const handleManageCoursesAccessClick = (school: any) => {
    openManageCoursesAccess({ schoolId: school.id });
  };

  const handleIssueCertificateForCourseClick = async (course: any) => {
    if (!currentStudent || !isCourseFullyCompleted(course)) {
      return;
    }

    const courseId = course.id ?? course._id;

    try {
      setIssuingCertificateCourseId(courseId);

      await dispatch(
        issueStudentCertificate({
          studentId: currentStudent.id,
          courseId,
        }),
      ).unwrap();

      await dispatch(getStudentById(currentStudent.id)).unwrap();

      showToast({
        data: {
          variant: 'success',
          title: getTranslation(
            'student_page.issue_certificate.toast.success_title',
          ),
          text: getTranslation(
            'student_page.issue_certificate.toast.success_text',
          ),
        },
      });
    } catch (error) {
      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getUnknownErrorMessage(
            error,
            getTranslation('common.toast.error.text'),
          ),
        },
      });
    } finally {
      setIssuingCertificateCourseId(null);
    }
  };

  const handleCourseClick = (course: any) => {
    navigate(`/courses/${course.id}`);
  };

  const studentSchools = getStudentSchools({ user, currentStudent });

  const schoolsTabs = studentSchools.map((school: any) => ({
    label: school.name,
    content: (
      <School
        locale={i18n.language}
        isRootAdmin
        courses={currentStudent.courses.filter(
          (course: any) => course.schoolId === school.id,
        )}
        school={school}
        student={currentStudent}
        status={currentStudent.status.find(
          (item: any) => item.schoolId === school.id,
        )}
        createdAt={currentStudent.createdAt}
        updatedAt={getLastUpdatedByCourses(currentStudent.courses)}
        onCourseClick={handleCourseClick}
        onManageCoursesAccessClick={() =>
          handleManageCoursesAccessClick(school)
        }
        onGrantProAccessClick={() => handleGrantProAccessClick(school)}
        onIssueCertificateForCourseClick={handleIssueCertificateForCourseClick}
        issuingCertificateCourseId={issuingCertificateCourseId}
      />
    ),
  }));

  const isRootAdmin = user.role === USER_ROLES.ROOT_ADMIN;

  return (
    <>
      <StudentPage
        locale={i18n.language}
        loading={loading}
        isRootAdmin={isRootAdmin}
        schoolsTabs={schoolsTabs}
        studentSchools={studentSchools}
        currentStudent={currentStudent}
        onEditClick={handleEditClick}
        onCourseClick={handleCourseClick}
        onManageCoursesAccessClick={handleManageCoursesAccessClick}
      />
      <EditStudent open={editStudentOpen} onClose={closeEditStudent} />
      <GrantProAccess
        {...grantProAccessData}
        open={grantProAccessOpen}
        onClose={closeGrantProAccess}
      />
      <ManageCoursesAccess
        {...manageCoursesAccessData}
        open={manageCoursesAccessOpen}
        onClose={closeManageCoursesAccess}
      />
    </>
  );
};

export default React.memo(StudentPageContainer) as any;
